import React from 'react';
import { ClothingItem } from '../../services/clothing';
import { getImageUrl } from '../../utils/imageUrl';

interface OutfitPreviewProps {
  items: { [category: string]: ClothingItem | null };
}

// Define category order and positioning for the outfit preview
const CATEGORY_ORDER = [
  'hat',
  'accessory', // bags, jewelry
  'outerwear', // jacket, coat, blazer
  'top', // shirt, t-shirt, blouse, sweater, hoodie, tank top
  'dress', // dress, jumpsuit
  'bottom', // pants, jeans, shorts, skirt
  'shoes', // sneakers, boots, sandals, heels
];

// Map category names to standardized keys
const CATEGORY_MAP: { [key: string]: string } = {
  't-shirt': 'top',
  'shirt': 'top',
  'blouse': 'top',
  'sweater': 'top',
  'hoodie': 'top',
  'tank top': 'top',
  'pants': 'bottom',
  'jeans': 'bottom',
  'shorts': 'bottom',
  'skirt': 'bottom',
  'dress': 'dress',
  'jumpsuit': 'dress',
  'jacket': 'outerwear',
  'coat': 'outerwear',
  'blazer': 'outerwear',
  'sneakers': 'shoes',
  'boots': 'shoes',
  'sandals': 'shoes',
  'heels': 'shoes',
  'hat': 'hat',
  'bag': 'accessory',
  'belt': 'accessory',
  'jewelry': 'accessory',
};

export const OutfitPreview: React.FC<OutfitPreviewProps> = ({ items }) => {
  try {
    // Normalize category names
    const normalizedItems: { [category: string]: ClothingItem | null } = {};
    Object.entries(items).forEach(([category, item]) => {
      try {
        if (item) {
          if (!item.category) {
            console.warn(`Item missing category:`, item);
            return;
          }
          const normalizedCategory = CATEGORY_MAP[item.category.toLowerCase()] || item.category.toLowerCase();
          normalizedItems[normalizedCategory] = item;
        }
      } catch (err) {
        console.error(`Error normalizing item for category ${category}:`, err, item);
      }
    });

    const getItemForCategory = (category: string): ClothingItem | null => {
      try {
        return normalizedItems[category] || null;
      } catch (err) {
        console.error(`Error getting item for category ${category}:`, err);
        return null;
      }
    };

    const getItemImageUrl = (item: ClothingItem | null): string | null => {
      try {
        if (!item) return null;
        if (!item.image_url) {
          console.warn(`Item missing image_url:`, item);
          return null;
        }
        return getImageUrl(item.image_url);
      } catch (err) {
        console.error(`Error getting image URL for item:`, err, item);
        return null;
      }
    };

    return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Human silhouette container */}
      <div className="relative bg-gradient-to-b from-gray-50 to-gray-100 rounded-2xl p-8" style={{ aspectRatio: '3/4', minHeight: '450px' }}>
        {/* Human silhouette outline */}
        <svg
          viewBox="0 0 200 400"
          className="absolute inset-0 w-full h-full p-4"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Head */}
          <ellipse cx="100" cy="50" rx="30" ry="35" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
          
          {/* Neck */}
          <rect x="90" y="80" width="20" height="15" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
          
          {/* Torso */}
          <path
            d="M 70 95 Q 70 95 70 200 Q 70 280 100 280 Q 130 280 130 200 Q 130 95 130 95"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
          
          {/* Arms */}
          <path
            d="M 70 120 Q 40 140 30 180 Q 25 200 30 220"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
          <path
            d="M 130 120 Q 160 140 170 180 Q 175 200 170 220"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
          
          {/* Legs */}
          <path
            d="M 100 280 Q 85 320 80 380"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
          <path
            d="M 100 280 Q 115 320 120 380"
            fill="#f3f4f6"
            stroke="#d1d5db"
            strokeWidth="2"
          />
        </svg>

        {/* Layered clothing items */}
        {CATEGORY_ORDER.map((category) => {
          try {
            const item = getItemForCategory(category);
            if (!item) return null;

            const imageUrl = getItemImageUrl(item);
            if (!imageUrl) {
              console.warn(`No image URL for ${category} item:`, item);
              return null;
            }

            // Position items based on category - adjusted for better visual layout
            const positions: { [key: string]: { top: string; left: string; width: string; height: string; zIndex: number; transform?: string } } = {
              hat: { top: '8%', left: '32%', width: '36%', height: '18%', zIndex: 10 },
              accessory: { top: '18%', left: '5%', width: '18%', height: '22%', zIndex: 9 },
              outerwear: { top: '18%', left: '22%', width: '56%', height: '48%', zIndex: 8 },
              top: { top: '20%', left: '28%', width: '44%', height: '42%', zIndex: 7 },
              dress: { top: '20%', left: '22%', width: '56%', height: '62%', zIndex: 7 },
              bottom: { top: '52%', left: '28%', width: '44%', height: '38%', zIndex: 6 },
              shoes: { top: '82%', left: '32%', width: '36%', height: '14%', zIndex: 5 },
            };

            const pos = positions[category];
            if (!pos) {
              console.warn(`No position defined for category: ${category}`);
              return null;
            }

            return (
              <div
                key={category}
                className="absolute"
                style={{
                  top: pos.top,
                  left: pos.left,
                  width: pos.width,
                  height: pos.height,
                  zIndex: pos.zIndex,
                  transform: pos.transform,
                }}
              >
                <img
                  src={imageUrl}
                  alt={item.category}
                  className="w-full h-full object-contain"
                  style={{ 
                    filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))',
                  }}
                  onError={(e) => {
                    console.error(`Failed to load image for ${item.category} (${item.id}):`, imageUrl);
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                  onLoad={() => {
                    console.debug(`Successfully loaded image for ${item.category} (${item.id})`);
                  }}
                />
              </div>
            );
          } catch (err) {
            console.error(`Error rendering item for category ${category}:`, err);
            return null;
          }
        })}
      </div>

      {/* Category labels */}
      <div className="mt-6 space-y-2 bg-white rounded-xl p-4">
        {CATEGORY_ORDER.map((category) => {
          try {
            const item = getItemForCategory(category);
            if (!item) return null;
            return (
              <div key={category} className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-600 capitalize">{category}:</span>
                <span className="text-gray-900 font-semibold">{item.subcategory || item.category}</span>
              </div>
            );
          } catch (err) {
            console.error(`Error rendering label for category ${category}:`, err);
            return null;
          }
        })}
      </div>
    </div>
    );
  } catch (err) {
    console.error('Error in OutfitPreview component:', err);
    return (
      <div className="w-full max-w-md mx-auto p-4 bg-red-50 rounded-xl border border-red-200">
        <p className="text-red-600 text-sm">Error displaying outfit preview. Please try again.</p>
        <p className="text-red-500 text-xs mt-2">{err instanceof Error ? err.message : 'Unknown error'}</p>
      </div>
    );
  }
};

